/**
 * Health Questionnaire — Answer management, question definitions, and modal rendering
 * Pure data functions (testable without DOM) + modal UI functions (DOM-dependent)
 */

// --- Question Definitions ---

export const QUESTIONS = [
  // Quick mode (5 questions)
  {
    id: 'budget',
    mode: 'quick',
    text: 'What is your monthly budget for health insurance?',
    options: [
      { value: 'under_200', label: 'Under $200' },
      { value: '200_400', label: '$200 - $400' },
      { value: '400_700', label: '$400 - $700' },
      { value: '700_plus', label: '$700+' },
      { value: 'unsure', label: 'Not sure' },
    ],
  },
  {
    id: 'doctorVisits',
    mode: 'quick',
    text: 'How often do you visit the doctor?',
    options: [
      { value: 'rarely', label: 'Rarely' },
      { value: 'occasionally', label: 'Occasionally' },
      { value: 'frequently', label: 'Frequently' },
    ],
  },
  {
    id: 'prescriptions',
    mode: 'quick',
    text: 'Do you take any prescription medications?',
    expandsTo: 'medications',
    options: [
      { value: 'none', label: 'None' },
      { value: 'yes', label: 'Yes' },
    ],
  },
  {
    id: 'conditions',
    mode: 'quick',
    text: 'Do you have any pre-existing conditions?',
    options: [
      { value: 'none', label: 'None' },
      { value: 'minor', label: 'Minor' },
      { value: 'significant', label: 'Significant' },
    ],
  },
  {
    id: 'priority',
    mode: 'quick',
    text: 'What is most important to you in a health plan?',
    options: [
      { value: 'lowest_price', label: 'Lowest price' },
      { value: 'best_coverage', label: 'Best coverage' },
      { value: 'keep_doctors', label: 'Keep my doctors' },
      { value: 'balanced', label: 'Balance' },
    ],
  },
  // Detailed mode (5 questions)
  {
    id: 'conditionsList',
    mode: 'detailed',
    text: 'Which conditions apply to you? (Select all that apply)',
    multiSelect: true,
    options: [
      { value: 'diabetes', label: 'Diabetes' },
      { value: 'heart_disease', label: 'Heart disease' },
      { value: 'cancer_history', label: 'Cancer history' },
      { value: 'asthma_copd', label: 'Asthma/COPD' },
      { value: 'mental_health', label: 'Mental health' },
      { value: 'joint_back', label: 'Joint/Back' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    id: 'plannedSurgery',
    mode: 'detailed',
    text: 'Do you have any planned surgeries or procedures?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'possibly', label: 'Possibly' },
    ],
  },
  {
    id: 'dentalVision',
    mode: 'detailed',
    text: 'Are you interested in dental or vision coverage?',
    options: [
      { value: 'dental', label: 'Dental' },
      { value: 'vision', label: 'Vision' },
      { value: 'both', label: 'Both' },
      { value: 'neither', label: 'Neither' },
    ],
  },
  {
    id: 'accidentCritical',
    mode: 'detailed',
    text: 'Are you interested in accident or critical illness coverage?',
    options: [
      { value: 'both', label: 'Both' },
      { value: 'accident', label: 'Accident only' },
      { value: 'critical', label: 'Critical illness only' },
      { value: 'neither', label: 'Not interested' },
    ],
  },
  {
    id: 'networkPreference',
    mode: 'detailed',
    text: 'How important is provider network flexibility?',
    options: [
      { value: 'restricted', label: 'Yes (EPO/HMO)' },
      { value: 'some_flexibility', label: 'Some flexibility (PPO/POS)' },
      { value: 'full_flexibility', label: 'Full flexibility' },
    ],
  },
];

// --- Pure Data Functions ---

/**
 * Create a new empty health profile with default answers
 * @returns {Object} Fresh health profile
 */
export function createHealthProfile() {
  return {
    mode: 'quick',
    completedAt: null,
    answers: {
      budget: null,
      doctorVisits: null,
      prescriptions: null,
      medications: [],
      conditions: null,
      priority: null,
      conditionsList: null,
      plannedSurgery: null,
      dentalVision: null,
      accidentCritical: null,
      networkPreference: null,
    },
    pivotCompliance: null,
    recommendedBundleSelected: null,
  };
}

/**
 * Set an answer on the profile
 * @param {Object} profile
 * @param {string} key - Answer key
 * @param {*} value - Answer value
 */
export function setAnswer(profile, key, value) {
  profile.answers[key] = value;
}

/**
 * Get an answer from the profile
 * @param {Object} profile
 * @param {string} key - Answer key
 * @returns {*} Answer value
 */
export function getAnswer(profile, key) {
  return profile.answers[key];
}

/**
 * Add a medication to the profile
 * @param {Object} profile
 * @param {{ name: string, dosage?: string, type?: string }} medication
 */
export function addMedication(profile, medication) {
  profile.answers.medications.push(medication);
}

/**
 * Remove a medication by index
 * @param {Object} profile
 * @param {number} index
 */
export function removeMedication(profile, index) {
  if (index >= 0 && index < profile.answers.medications.length) {
    profile.answers.medications.splice(index, 1);
  }
}

/**
 * Get the medications array
 * @param {Object} profile
 * @returns {Object[]}
 */
export function getMedications(profile) {
  return profile.answers.medications;
}

/**
 * Check if the profile is in detailed mode
 * @param {Object} profile
 * @returns {boolean}
 */
export function isDetailedMode(profile) {
  return profile.mode === 'detailed';
}

/**
 * Switch the profile to detailed mode
 * @param {Object} profile
 */
export function switchToDetailed(profile) {
  profile.mode = 'detailed';
}

/**
 * Get a plain object summary of answers for persistence
 * @param {Object} profile
 * @returns {Object} Serializable summary
 */
export function getAnswersSummary(profile) {
  return {
    mode: profile.mode,
    completedAt: profile.completedAt,
    ...structuredClone(profile.answers),
  };
}

// --- Modal UI Functions (DOM-dependent) ---

let _currentProfile = null;
let _onComplete = null;

function cloneProfile(profile) {
  if (!profile) return createHealthProfile();
  return {
    ...profile,
    answers: {
      ...createHealthProfile().answers,
      ...(structuredClone(profile.answers || {})),
    },
  };
}

/**
 * ESC key handler for modal
 */
function modalEscHandler(e) {
  if (e.key === 'Escape') hideModal();
}

/**
 * Show the health questionnaire modal
 * @param {{ onComplete: Function, initialProfile?: Object }} options
 */
export function showModal({ onComplete, initialProfile } = {}) {
  _currentProfile = cloneProfile(initialProfile);
  _currentProfile.mode = 'detailed';
  _onComplete = onComplete || null;

  const modal = document.getElementById('wq-health-modal');
  if (!modal) return;

  modal.classList.add('wq-modal--open');
  document.addEventListener('keydown', modalEscHandler);
  renderAssessmentForm();
}

/**
 * Hide the health questionnaire modal
 */
export function hideModal() {
  const modal = document.getElementById('wq-health-modal');
  if (!modal) return;

  modal.classList.remove('wq-modal--open');
  document.removeEventListener('keydown', modalEscHandler);

  _currentProfile = null;
  _onComplete = null;
}

function clearBody() {
  const body = document.getElementById('wq-health-body');
  if (!body) return;

  while (body.firstChild) {
    body.removeChild(body.firstChild);
  }
  return body;
}

function createSectionTitle(text) {
  const title = document.createElement('h3');
  title.className = 'wq-health-section-title';
  title.textContent = text;
  return title;
}

function renderAssessmentForm() {
  const body = clearBody();
  if (!body || !_currentProfile) return;

  const quickSection = document.createElement('section');
  quickSection.className = 'wq-health-section';
  quickSection.appendChild(createSectionTitle('Core preferences'));

  QUESTIONS.filter(q => q.mode === 'quick').forEach(question => {
    quickSection.appendChild(renderQuestionField(question));
  });

  const detailSection = document.createElement('section');
  detailSection.className = 'wq-health-section';
  detailSection.appendChild(createSectionTitle('Additional details'));

  QUESTIONS.filter(q => q.mode === 'detailed').forEach(question => {
    detailSection.appendChild(renderQuestionField(question));
  });

  body.appendChild(quickSection);
  body.appendChild(detailSection);
  body.appendChild(renderMedicationPanel());
  body.appendChild(renderAssessmentActions());
}

function renderQuestionField(question) {
  const field = document.createElement('div');
  field.className = 'wq-health-field';

  const label = document.createElement('h4');
  label.className = 'wq-health-field__label';
  label.textContent = question.text;
  field.appendChild(label);

  const optionsContainer = document.createElement('div');
  optionsContainer.className = 'wq-health-options';

  if (question.multiSelect) {
    renderMultiSelectOptions(question, optionsContainer);
  } else {
    renderSingleSelectOptions(question, optionsContainer);
  }

  field.appendChild(optionsContainer);
  return field;
}

/**
 * Render single-select option buttons
 */
function renderSingleSelectOptions(question, container) {
  const currentValue = getAnswer(_currentProfile, question.id);

  for (const option of question.options) {
    const btn = document.createElement('button');
    btn.textContent = option.label;
    btn.className = 'wq-health-option';
    btn.dataset.value = option.value;
    btn.type = 'button';
    if (currentValue === option.value) {
      btn.classList.add('wq-health-option--selected');
    }

    btn.addEventListener('click', () => {
      setAnswer(_currentProfile, question.id, option.value);
      Array.from(container.querySelectorAll('.wq-health-option')).forEach(optionBtn => {
        optionBtn.classList.toggle('wq-health-option--selected', optionBtn.dataset.value === option.value);
      });
    });

    container.appendChild(btn);
  }
}

/**
 * Render multi-select option buttons with toggle behavior
 */
function renderMultiSelectOptions(question, container) {
  let selected = new Set(getAnswer(_currentProfile, question.id) || []);

  for (const option of question.options) {
    const btn = document.createElement('button');
    btn.textContent = option.label;
    btn.className = 'wq-health-option';
    btn.dataset.value = option.value;
    btn.type = 'button';
    if (selected.has(option.value)) {
      btn.classList.add('wq-health-option--selected');
    }

    btn.addEventListener('click', () => {
      if (option.value === 'none') {
        // "None" clears all others
        selected.clear();
        selected.add('none');
      } else {
        // Remove "none" if selecting something else
        selected.delete('none');
        if (selected.has(option.value)) {
          selected.delete(option.value);
        } else {
          selected.add(option.value);
        }
      }

      // Update visual state
      const buttons = container.querySelectorAll('.wq-health-option');
      for (const b of buttons) {
        if (selected.has(b.dataset.value)) {
          b.classList.add('wq-health-option--selected');
        } else {
          b.classList.remove('wq-health-option--selected');
        }
      }

      setAnswer(_currentProfile, question.id, [...selected]);
    });

    container.appendChild(btn);
  }
}

function renderMedicationPanel() {
  const section = document.createElement('section');
  section.className = 'wq-health-section';
  section.appendChild(createSectionTitle('Medications'));

  const caption = document.createElement('p');
  caption.className = 'wq-health-med-caption';
  caption.textContent = 'Add medications as they are identified. This can be updated later.';
  section.appendChild(caption);

  const listContainer = document.createElement('div');
  listContainer.className = 'wq-health-med-list';
  section.appendChild(listContainer);

  function renderMedList() {
    while (listContainer.firstChild) {
      listContainer.removeChild(listContainer.firstChild);
    }
    const meds = getMedications(_currentProfile);
    for (let i = 0; i < meds.length; i++) {
      const row = document.createElement('div');
      row.className = 'wq-health-med-row';

      const label = document.createElement('span');
      label.textContent = meds[i].name + (meds[i].dosage ? ` (${meds[i].dosage})` : '');
      row.appendChild(label);

      const removeBtn = document.createElement('button');
      removeBtn.textContent = 'Remove';
      removeBtn.className = 'wq-health-med-remove';
      removeBtn.addEventListener('click', () => {
        removeMedication(_currentProfile, i);
        renderMedList();
      });
      row.appendChild(removeBtn);

      listContainer.appendChild(row);
    }

    if (meds.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.className = 'wq-health-med-empty';
      emptyState.textContent = 'No medications added';
      listContainer.appendChild(emptyState);
    }
  }

  const form = document.createElement('div');
  form.className = 'wq-health-med-form';

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.placeholder = 'Medication name (required)';
  nameInput.className = 'wq-health-med-name';
  form.appendChild(nameInput);

  const dosageInput = document.createElement('input');
  dosageInput.type = 'text';
  dosageInput.placeholder = 'Dosage (optional)';
  dosageInput.className = 'wq-health-med-dosage';
  form.appendChild(dosageInput);

  const typeSelect = document.createElement('select');
  typeSelect.className = 'wq-health-med-type';
  const typeOptions = [
    { value: 'not_sure', label: 'Not sure' },
    { value: 'generic', label: 'Generic' },
    { value: 'brand', label: 'Brand' },
  ];
  for (const opt of typeOptions) {
    const optEl = document.createElement('option');
    optEl.value = opt.value;
    optEl.textContent = opt.label;
    typeSelect.appendChild(optEl);
  }
  form.appendChild(typeSelect);

  const addBtn = document.createElement('button');
  addBtn.textContent = '+ Add';
  addBtn.className = 'wq-health-med-add';
  addBtn.type = 'button';
  addBtn.addEventListener('click', () => {
    const name = nameInput.value.trim();
    if (!name) return;
    addMedication(_currentProfile, {
      name,
      dosage: dosageInput.value.trim() || undefined,
      type: typeSelect.value,
    });
    nameInput.value = '';
    dosageInput.value = '';
    typeSelect.value = 'not_sure';
    renderMedList();
  });
  form.appendChild(addBtn);

  section.appendChild(form);

  renderMedList();
  return section;
}

function renderAssessmentActions() {
  const actions = document.createElement('div');
  actions.className = 'wq-health-actions';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.className = 'wq-health-back';
  cancelBtn.addEventListener('click', hideModal);

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.textContent = 'Apply Assessment';
  saveBtn.className = 'wq-health-continue';
  saveBtn.addEventListener('click', finishQuestionnaire);

  actions.appendChild(cancelBtn);
  actions.appendChild(saveBtn);
  return actions;
}

/**
 * Finish the questionnaire — set completedAt and call onComplete
 */
function finishQuestionnaire() {
  if (!_currentProfile) return;

  _currentProfile.completedAt = new Date().toISOString();

  const result = {
    profile: _currentProfile,
    answers: getAnswersSummary(_currentProfile),
  };

  if (typeof _onComplete === 'function') {
    _onComplete(result);
  }

  hideModal();
}

// --- Namespace Export ---

export const HealthQuestionnaire = {
  createHealthProfile,
  setAnswer,
  getAnswer,
  addMedication,
  removeMedication,
  getMedications,
  isDetailedMode,
  switchToDetailed,
  getAnswersSummary,
  QUESTIONS,
  showModal,
  hideModal,
};
